// ============================================================================
// LES TEXTES DE L'INTERFACE DU BACK-OFFICE — le moteur partagé, câblé à cette application.
//
// UTILITÉ : le moteur vit dans `@orgazone/i18n` (il ne connaît ni nos langues ni nos textes) ; ce
// module lui dit quelles langues existent, et lui apporte les catalogues. Les écrans, eux, n'appellent
// que `t()` et `nombre()`.
//
// ⚠️ ON NE CHARGE QUE LA LANGUE ACTIVE, et c'est mesuré : 122 octets par entrée, soit ~366 Ko par
//    langue pour les trois mille textes du back-office. Les charger toutes ferait porter à chaque
//    utilisateur deux langues qu'il ne lira jamais. Un compte FRANÇAIS ne télécharge RIEN — le français
//    est la clé, et son catalogue ne porte que ses pluriels (2,3 Ko, statiques).
// ⚠️ LE CHARGEMENT SE FAIT AVANT LE PREMIER RENDU, dans la séquence d'amorçage qui pose déjà la langue
//    (`auth.tsx`) : sans cela, l'écran afficherait le français puis se corrigerait sous les yeux de
//    l'utilisateur — le flash que §23.0 interdit.
// ⚠️ LES CATALOGUES SONT DÉCOUPÉS PAR ÉCRAN, et pas seulement par langue. Aujourd'hui les 45 écrans du
//    back-office sont importés STATIQUEMENT (`App.tsx`) : tout part dans un seul paquet, donc ce
//    découpage n'allège rien — et il faut le dire. Le jour où les écrans passeront en `lazy()`, les
//    fragments suivront AUTOMATIQUEMENT dans le même morceau, sans aucune plomberie asynchrone.
// ============================================================================
import { formatNumber, key, keyText, registerCatalogue, t, type DeclaredKey, type Translatable } from '@orgazone/i18n';
// ⚠️ C'est l'import de ce module qui CONFIGURE le moteur (langues du contrat) : il doit précéder tout
//    enregistrement de catalogue, et la porte de la langue est le bon endroit pour cette déclaration.
import { platformLang } from './lang';
// ⚠️ HORS du dossier des catalogues, et c'est VOULU : le motif de l'import dynamique est `./i18n/${lang}`,
//    et un fichier `fr.ts` qui s'y trouverait serait pris dans le glob — le bundler avertissait alors
//    qu'un module statiquement ET dynamiquement importé ne se déplace pas. Les pluriels français ne
//    sont pas un catalogue de langue à charger : ils voyagent avec le code.
import { FR } from './i18n-pluriels-fr';
// ⚠️ LES PLURIELS DU PAQUET PARTAGÉ VOYAGENT AVEC LUI : `ui-web` porte ses propres phrases comptées
//    (« Filtres — {n} actif »), et l'application les joint aux siennes. Sans ce fragment la langue de
//    RÉFÉRENCE afficherait « Filtres — 1 actifs » — la langue de la quasi-totalité des comptes.
import { UI_WEB_FR } from '@orgazone/ui-web/i18n-fr';

// ⚠️ Les PLURIELS français sont statiques et minuscules : sans eux, la langue de référence afficherait
//    « 3 ligne ». Les charger à la demande coûterait un aller-retour pour deux kilo-octets.
registerCatalogue(platformLang(), { ...UI_WEB_FR, ...FR });

/** A-t-on déjà chargé cette langue ? Un second chargement serait un aller-retour pour rien. */
const chargees = new Set<string>([platformLang()]);

/**
 * Charge le catalogue de la langue demandée — et lui seul.
 * ⚠️ À ATTENDRE AVANT LE PREMIER RENDU. Le `import()` est analysé par le bundler grâce à sa partie
 *    fixe : il produit un morceau PAR LANGUE, et le navigateur n'en télécharge qu'un.
 * ⚠️ UN ÉCHEC DE CHARGEMENT NE FAIT PAS TOMBER L'APPLICATION : l'interface reste dans la langue de
 *    référence, ce qui est lisible, plutôt qu'un écran blanc. C'est un best-effort ASSUMÉ — le seul
 *    de ce module — parce qu'une langue manquante dégrade le confort, jamais la justesse.
 */
export async function loadCatalogue(lang: string): Promise<void> {
  if (chargees.has(lang)) return;
  try {
    const module = (await import(`./i18n/${lang}.ts`)) as { default: Parameters<typeof registerCatalogue>[1] };
    registerCatalogue(lang, module.default);
    chargees.add(lang);
  } catch {
    // On NE relance pas : l'appelant est la séquence d'amorçage, et l'application doit s'ouvrir.
  }
}

// ⚠️ `key` NE TRADUIT PAS : elle déclare qu'un texte français vivant dans une DONNÉE de module (la
//    table de navigation, des colonnes, des onglets) est une clé, traduite AU RENDU par `t()`. Sans
//    elle il fallait choisir entre figer le français au chargement et laisser des textes
//    indiscernables d'un oubli.
export { t, key, keyText, formatNumber as nombre };
export type { DeclaredKey, Translatable };
export { currentLang, missingTexts as manques } from '@orgazone/i18n';
export type { Catalogue, Translation as Traduction, Variables } from '@orgazone/i18n';
